import { Schema, z } from 'zod';

/**
 * Represents an italic styling
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/marks/em
 *
 * @typedef EmMark
 * @property - type : type of node
 */
export type EmMark = {
  readonly type: 'em';
};

export const EmMarkSchema: Schema<EmMark> = z.object({
  type: z.literal('em')
});
/**
 * Represents a bold styling
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/marks/strong
 *
 * @typedef StrongMark
 * @property - type : type of node
 */
export type StrongMark = {
  readonly type: 'strong';
};

export const StrongMarkSchema: Schema<StrongMark> = z.object({
  type: z.literal('strong')
});
/**
 * Represents an underline styling
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/marks/underline
 *
 * @typedef UnderlineMark
 * @property - type : type of node
 */
export type UnderlineMark = {
  readonly type: 'underline';
};

export const UnderlineMarkSchema: Schema<UnderlineMark> = z.object({
  type: z.literal('underline')
});

/**
 * Represents additional styling for links
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/marks/link/#attributes
 *
 * @typedef LinkMarkAttributes
 * @property - href : URI
 * @property - title
 * @property - id
 * @property - collection
 * @property - occurrenceKey
 */
export type LinkMarkAttributes = {
  readonly href: string;
  readonly title?: string;
  readonly id?: string;
  readonly collection?: string;
  readonly occurrenceKey?: string;
};

export const LinkMarkAttributesSchema: Schema<LinkMarkAttributes> = z.object({
  href: z.string(),
  title: z.string().optional(),
  id: z.string().optional(),
  collection: z.string().optional(),
  occurrenceKey: z.string().optional()
});

/**
 * Represents a hyperlink
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/marks/link
 *
 * @typedef LinkMark
 * @property - type : type of node
 * @property - attrs : additional styling for the link
 */
export type LinkMark = {
  readonly type: 'link';
  readonly attrs: LinkMarkAttributes;
};

export const LinkMarkSchema: Schema<LinkMark> = z.object({
  type: z.literal('link'),
  attrs: LinkMarkAttributesSchema
});

/**
 * Represents additional styling for string values
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/structure/#marks
 *
 * @typedef Mark
 */
export type Mark = EmMark | LinkMark | StrongMark | UnderlineMark;

export const MarkSchema = z.union([
  EmMarkSchema,
  LinkMarkSchema,
  StrongMarkSchema,
  UnderlineMarkSchema
]);

/**
 * Represents a text
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/nodes/text
 *
 * @typedef Text
 * @property - type : type of node
 * @property - text : text value
 * @property - marks : array of additional styling, @typedef Mark , for the text
 */
export type Text = {
  readonly type: 'text';
  readonly text: string;
  readonly marks?: readonly Mark[];
};

export const TextSchema: Schema<Text> = z.object({
  type: z.literal('text'),
  text: z.string(),
  marks: z.array(MarkSchema).optional()
});

/**
 * Represents a new line in a text string
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/nodes/hardBreak
 *
 * @typedef HardBreak
 * @property - type : type of node
 */
export type HardBreak = {
  readonly type: 'hardBreak';
};

export const HardBreakSchema: Schema<HardBreak> = z.object({
  type: z.literal('hardBreak')
});

/**
 * Inline Node consists of nodes that can be used within a TopLevelNode
 * eg : Paragraph -> Text
 * Only the supported top level nodes are listed below.
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/structure/#inline-nodes
 *
 * @typedef InlineNode
 */
export type InlineNode = HardBreak | Text;

export const InlineNodeSchema = z.union([HardBreakSchema, TextSchema]);

/**
 * Represents unordered list
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/nodes/bulletList
 *
 * @typedef BulletList
 * @property - type : type of node
 * @property - content : array containing ListItem nodes
 */
export type BulletList = {
  readonly type: 'bulletList';
  readonly content: readonly ListItem[];
};

export const BulletListSchema: Schema<BulletList> = z.object({
  type: z.literal('bulletList'),
  content: z.array(z.lazy(() => ListItemSchema))
});

/**
 * Represents heading attributes
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/nodes/heading/#attributes
 *
 * @typedef HeadingAttributes
 * @property - level : represents depth of heading
 */

export const HeadingAttributesSchema = z.object({
  level: z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6)
  ])
});
export type HeadingAttributes = z.infer<typeof HeadingAttributesSchema>;

/**
 * Represents heading
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/nodes/heading
 *
 * @typedef Heading
 * @property - type : type of node
 * @property - content : array containing @typedef InlineNode
 */
export type Heading = {
  readonly type: 'heading';
  readonly attrs: HeadingAttributes;
  readonly content?: readonly InlineNode[];
};

export const HeadingSchema: Schema<Heading> = z.object({
  type: z.literal('heading'),
  attrs: HeadingAttributesSchema,
  content: z.array(InlineNodeSchema).optional()
});

/**
 * Represents attributes for an ordered list
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/nodes/orderedList/#attributes
 *
 * @typedef OrderedListAttributes
 * @property - order : number of the first item in the list. When not specified, the list starts from 1.
 * eg : order of 3 would mean the list starts at number three.
 */
export type OrderedListAttributes = {
  readonly order?: number;
};

export const OrderedListAttributesSchema: Schema<OrderedListAttributes> =
  z.object({
    order: z.number().optional()
  });

/**
 * Represents an ordered list
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/nodes/orderedList
 *
 * @typedef OrderedList
 * @property - type : type of node
 * @property - content : array containing ListItem nodes
 * @property - attrs : attributes for an ordered list
 */
export type OrderedList = {
  readonly type: 'orderedList';
  readonly content: readonly ListItem[];
  readonly attrs?: OrderedListAttributes;
};

export const OrderedListSchema: Schema<OrderedList> = z.object({
  type: z.literal('orderedList'),
  content: z.array(z.lazy(() => ListItemSchema)),
  attrs: z.optional(OrderedListAttributesSchema)
});

/**
 * Represents a paragraph
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/nodes/paragraph
 *
 * @typedef Paragraph
 * @property - type : type of node
 * @property - content : array containing @typedef InlineNode
 */
export type Paragraph = {
  readonly type: 'paragraph';
  readonly content?: readonly InlineNode[];
};

export const ParagraphSchema = z.object({
  type: z.literal('paragraph'),
  content: z.array(InlineNodeSchema).optional()
});

/**
 * Represents child list item of BulletList and OrderedList
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/nodes/listItem
 *
 * @typedef ListItem
 * @property - type : type of node
 * @property - content : array containing sub BulletList, sub OrderedList, or Paragraph nodes
 */
export type ListItem = {
  readonly type: 'listItem';
  readonly content: readonly (BulletList | OrderedList | Paragraph)[];
};

export const ListItemSchema: Schema<ListItem> = z.object({
  type: z.literal('listItem'),
  content: z.array(
    z.union([BulletListSchema, OrderedListSchema, ParagraphSchema])
  )
});

/**
 * Represents a divider
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/nodes/rule
 *
 * @typedef Rule
 * @property - type : type of node
 */
export type Rule = {
  readonly type: 'rule';
};

export const RuleSchema: Schema<Rule> = z.object({
  type: z.literal('rule')
});

/**
 * Top Level Node consists of nodes that can be used at the root content level
 * Only the supported top level nodes are listed below
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/structure/#top-level-block-nodes
 *
 * @typedef TopLevelNode
 */
export type TopLevelNode =
  | BulletList
  | Heading
  | OrderedList
  | Paragraph
  | Rule;

export const TopLevelNodeSchema: Schema<TopLevelNode> = z.union([
  BulletListSchema,
  HeadingSchema,
  OrderedListSchema,
  ParagraphSchema,
  RuleSchema
]);

/**
 * Atlassian Document Format (ADF) - Format for representing rich text
 * https://developer.atlassian.com/cloud/jira/platform/apis/document/structure/#atlassian-document-format
 *
 * @typedef ADF
 * @property - version : version of ADF used
 * @property - type : type of node
 * @property - content : array containing @typedef TopLevelNode
 */
export type ADF = {
  version: 1;
  type: 'doc';
  content: readonly TopLevelNode[];
};

export const ADFSchema: Schema<ADF> = z.object({
  version: z.literal(1),
  type: z.literal('doc'),
  content: z.array(TopLevelNodeSchema)
});
